import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import {
  Stepper,
  StepperIndicator,
  StepperItem,
  StepperNav,
  StepperSeparator,
  StepperTitle,
  StepperTrigger,
} from '@/components/ui/stepper'
import type { ExtractDialogStep } from '../hooks/useStandardSectionExtraction'

type StandardSectionExtractStepperProps = {
  steps: ExtractDialogStep[]
  currentStep: ExtractDialogStep
  skippedSteps?: ExtractDialogStep[]
  orientation?: 'horizontal' | 'vertical'
}

export function StandardSectionExtractStepper({
  steps,
  currentStep,
  skippedSteps = [],
  orientation = 'horizontal',
}: StandardSectionExtractStepperProps) {
  const { t } = useTranslation('standardsLibrary')
  const skippedStepSet = new Set(skippedSteps)

  const stepLabels: Record<ExtractDialogStep, string> = {
    extract: t('detail.extractDialog.stepExtract'),
    acknowledgeNotFound: t('detail.extractDialog.stepAcknowledge'),
    review: t('detail.extractDialog.stepReview'),
    translate: t('detail.extractDialog.stepTranslate'),
    reviewTranslations: t('detail.extractDialog.stepReviewTranslations'),
    save: t('detail.extractDialog.stepSave'),
  }

  if (orientation === 'vertical') {
    const currentIndex = steps.indexOf(currentStep)
    return (
      <nav
        className='flex flex-col'
        aria-label={t('detail.extractDialog.stepperNavLabel')}
      >
        {steps.map((step, index) => {
          const isSkipped = skippedStepSet.has(step)
          const isComplete = index < currentIndex && !isSkipped
          const isCurrent = step === currentStep
          return (
            <div key={step} className='flex items-start gap-3'>
              <div className='flex shrink-0 flex-col items-center'>
                <span
                  className={cn(
                    'flex size-8 items-center justify-center rounded-md border text-sm font-medium transition-colors',
                    isSkipped &&
                      'border-muted-foreground/25 bg-muted text-muted-foreground',
                    isComplete &&
                      'border-transparent bg-primary text-primary-foreground',
                    isCurrent &&
                      !isSkipped &&
                      'border-primary ring-2 ring-primary/30 ring-offset-2 text-foreground',
                    !isComplete &&
                      !isCurrent &&
                      !isSkipped &&
                      'border-primary/20 text-muted-foreground'
                  )}
                  aria-hidden
                >
                  {isComplete ? <Check className='size-3.5' /> : index + 1}
                </span>
                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      'my-1.5 min-h-8 w-px flex-1',
                      isComplete && !isSkipped ? 'bg-primary' : 'bg-border'
                    )}
                    aria-hidden
                  />
                )}
              </div>
              <div
                className={cn(
                  'pb-8 pt-1 last:pb-0',
                  isSkipped && 'opacity-45'
                )}
              >
                <span
                  className={cn(
                    'text-sm font-medium',
                    isSkipped && 'line-through',
                    isCurrent && !isSkipped
                      ? 'text-foreground'
                      : 'text-muted-foreground'
                  )}
                  title={
                    isSkipped
                      ? t('detail.extractDialog.stepSkippedTitle')
                      : undefined
                  }
                >
                  {stepLabels[step]}
                </span>
              </div>
            </div>
          )
        })}
      </nav>
    )
  }

  const stepDefs = steps.map((step) => ({ id: step, title: stepLabels[step] }))

  return (
    <Stepper
      steps={stepDefs}
      value={currentStep}
      orientation='horizontal'
      aria-label={t('detail.extractDialog.stepperNavLabel')}
    >
      <StepperNav>
        {stepDefs.map((stepDef, index) => {
          const isSkipped = skippedStepSet.has(stepDef.id as ExtractDialogStep)
          return (
            <StepperItem
              key={stepDef.id}
              stepId={stepDef.id}
              className={cn(isSkipped && 'opacity-45')}
            >
              <StepperTrigger asChild>
                <span className='inline-flex items-center gap-2 py-0.5'>
                  <StepperIndicator
                    variant='outline'
                    className={cn(
                      isSkipped &&
                        'border-muted-foreground/25 bg-transparent text-muted-foreground data-[state=completed]:border-muted-foreground/25 data-[state=completed]:text-muted-foreground'
                    )}
                  >
                    {index + 1}
                  </StepperIndicator>
                  <StepperTitle
                    className={cn(
                      'hidden sm:block',
                      isSkipped && 'line-through'
                    )}
                    title={
                      isSkipped
                        ? t('detail.extractDialog.stepSkippedTitle')
                        : undefined
                    }
                  >
                    {stepDef.title}
                  </StepperTitle>
                </span>
              </StepperTrigger>
              {index < stepDefs.length - 1 && <StepperSeparator />}
            </StepperItem>
          )
        })}
      </StepperNav>
    </Stepper>
  )
}
